import React, { useState, useEffect } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";
const G = "#0077B5";

export default function InviteAcceptPage({ user, onAccepted }) {
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [sendingMagic, setSendingMagic] = useState(false);
  const [consented, setConsented] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [alreadyInWorkspace, setAlreadyInWorkspace] = useState(false);

  const token = (() => {
    const match = window.location.hash.match(/[?&]token=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  })();

  useEffect(() => {
    if (!token) { setError("No invitation token found."); setLoading(false); return; }
    fetch(`${API_BASE}/app/api/auth/invite/validate?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(d => {
        if (d.valid) {
          setInvite(d);
          if (d.email) setLoginEmail(d.email);
        } else if (d.reason === "expired") {
          setError("expired");
          setInvite(d);
        } else {
          setError(d.reason || "Invalid invitation");
        }
        setLoading(false);
      })
      .catch(() => { setError("Could not validate invitation."); setLoading(false); });
  }, [token]);

  const handleAccept = async () => {
    if (!consented) { alert("Please check the consent box to continue."); return; }
    setAccepting(true);
    const st = localStorage.getItem("nectar_st");
    try {
      const r = await fetch(`${API_BASE}/app/api/auth/invite/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Session-Token": st },
        body: JSON.stringify({ token, consent: true }),
      });
      const d = await r.json();
      if (r.ok) {
        onAccepted();
      } else if (d.code === "already_in_workspace") {
        setAlreadyInWorkspace(true);
      } else {
        alert(d.error || "Failed to accept invitation");
      }
    } catch { alert("Network error"); }
    setAccepting(false);
  };

  const handleDecline = async () => {
    if (!window.confirm("Decline this invitation?")) return;
    try {
      const r = await fetch(`${API_BASE}/app/api/auth/invite/decline`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (r.ok) setDeclined(true);
      else { const d = await r.json(); alert(d.error || "Failed to decline"); }
    } catch { alert("Network error"); }
  };

  const handleSendMagicLink = async () => {
    if (!loginEmail.trim()) return;
    setSendingMagic(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/auth/magic-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginEmail.trim() }),
      });
      const d = await r.json();
      if (r.ok) setMagicSent(true);
      else alert(d.error || "Failed to send sign-in link");
    } catch { alert("Network error"); }
    setSendingMagic(false);
  };

  const requestNewInvite = async () => {
    if (!invite) return;
    try {
      await fetch(`${API_BASE}/app/api/auth/invite/request-new?workspace_id=${invite.workspace_id}&email=${encodeURIComponent(invite.email)}`);
      alert("Your admin has been notified. They can re-send your invitation from the team dashboard.");
    } catch { alert("Could not request new invite."); }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "80px 24px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
        <div style={{ fontSize: 14, color: "#999" }}>Validating your invitation...</div>
      </div>
    );
  }

  // Expired
  if (error === "expired") {
    return (
      <div style={{ maxWidth: 480, margin: "0 auto", padding: "60px 24px", textAlign: "center", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
        <div style={{ fontSize: 24, fontWeight: 500, marginBottom: 12 }}>Invitation expired</div>
        <p style={{ fontSize: 15, color: "#555", lineHeight: 1.6, marginBottom: 24 }}>
          Your invitation to join <strong>{invite?.workspace_name || "the team"}</strong> has expired.
        </p>
        <button onClick={requestNewInvite} style={{ background: G, color: "#fff", border: "none", borderRadius: 8, padding: "12px 24px", fontSize: 14, cursor: "pointer", fontWeight: 600 }}>
          Request a new invitation
        </button>
        <p style={{ fontSize: 12, color: "#999", marginTop: 12 }}>This will notify your admin to re-send the invite.</p>
      </div>
    );
  }

  // Generic error
  if (error) {
    return (
      <div style={{ maxWidth: 480, margin: "0 auto", padding: "60px 24px", textAlign: "center", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
        <div style={{ fontSize: 24, fontWeight: 500, marginBottom: 12 }}>Something went wrong</div>
        <p style={{ fontSize: 15, color: "#555", lineHeight: 1.6 }}>{error}</p>
      </div>
    );
  }

  // Not logged in — new users go to onboarding wizard, existing users sign in
  if (!user) {
    return (
      <div style={{ maxWidth: 440, margin: "0 auto", padding: "60px 24px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "inline-block", background: "#E1F5EE", color: "#0F6E56", padding: "4px 14px", borderRadius: 20, fontSize: 11, fontWeight: 500, marginBottom: 12 }}>Team Invitation</div>
          <div style={{ fontSize: 24, fontWeight: 500, marginBottom: 8 }}>
            Join <span style={{ color: "#1D9E75" }}>{invite.workspace_name || "the team"}</span> on BrandHives
          </div>
          <p style={{ fontSize: 14, color: "#555" }}>
            {invite.inviter_name ? `${invite.inviter_name} invited you.` : "You've been invited."}
          </p>
        </div>

        {/* New user — go to onboarding wizard */}
        <button
          onClick={() => { window.location.hash = `/invite-onboarding?token=${encodeURIComponent(token)}`; }}
          style={{ width: "100%", background: G, color: "#fff", border: "none", borderRadius: 8, padding: "14px", fontSize: 15, fontWeight: 600, cursor: "pointer", marginBottom: 16 }}
        >
          Get Started
        </button>
        <p style={{ fontSize: 12, color: "#999", textAlign: "center", marginBottom: 24 }}>
          Connect your LinkedIn, pick your topics, and set your voice. Takes 2 minutes.
        </p>

        {/* Existing user — sign in */}
        <div style={{ borderTop: "1px solid #eee", paddingTop: 20 }}>
          <p style={{ fontSize: 13, color: "#666", textAlign: "center", marginBottom: 12 }}>Already have a BrandHives account?</p>
          {!magicSent ? (
            <>
              <div style={{ marginBottom: 16 }}>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={e => setLoginEmail(e.target.value)}
                  placeholder={invite.email || "you@company.com"}
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 8, border: "1px solid #ddd", fontSize: 14, boxSizing: "border-box" }}
                />
              </div>
              <button
                onClick={handleSendMagicLink}
                disabled={sendingMagic || !loginEmail.trim()}
                style={{ width: "100%", background: "#fff", color: G, border: `1px solid ${G}`, borderRadius: 8, padding: "12px", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: (sendingMagic || !loginEmail.trim()) ? 0.6 : 1 }}
              >
                {sendingMagic ? "Sending..." : "Sign in with magic link"}
              </button>
            </>
          ) : (
            <div style={{ textAlign: "center", background: "#E1F5EE", borderRadius: 12, padding: "24px 20px" }}>
              <div style={{ fontSize: 18, fontWeight: 600, color: "#0F6E56", marginBottom: 8 }}>Check your email</div>
              <p style={{ fontSize: 14, color: "#555", lineHeight: 1.6 }}>
                We sent a sign-in link to <strong>{loginEmail}</strong>.<br />
                Click the link in the email, then come back here to accept the invitation.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Declined
  if (declined) {
    return (
      <div style={{ maxWidth: 440, margin: "0 auto", padding: "60px 24px", textAlign: "center", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
        <div style={{ fontSize: 24, fontWeight: 500, marginBottom: 12 }}>Invitation declined</div>
        <p style={{ fontSize: 15, color: "#555", lineHeight: 1.6 }}>You can close this page.</p>
      </div>
    );
  }

  // Logged in — show accept flow
  return (
    <div style={{ maxWidth: 440, margin: "0 auto", padding: "60px 24px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ display: "inline-block", background: "#E1F5EE", color: "#0F6E56", padding: "4px 14px", borderRadius: 20, fontSize: 11, fontWeight: 500, marginBottom: 12 }}>Team Invitation</div>
        <div style={{ fontSize: 24, fontWeight: 500, marginBottom: 8 }}>
          Join <span style={{ color: "#1D9E75" }}>{invite.workspace_name || "the team"}</span>
        </div>
        <p style={{ fontSize: 14, color: "#555" }}>
          {invite.inviter_name ? `${invite.inviter_name} invited you` : "You've been invited"} as a <strong>{invite.role || "member"}</strong>.
        </p>
      </div>

      <div style={{ background: "#f8f8f6", borderRadius: 12, padding: "20px", marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>As a team member you'll get:</div>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: "#555", lineHeight: 2 }}>
          <li>AI-written LinkedIn posts in your voice</li>
          <li>Automatic weekly scheduling</li>
          <li>Your own topics and writing style</li>
          <li>Weekly team plug posts</li>
        </ul>
      </div>

      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 20, cursor: "pointer" }}>
        <input type="checkbox" checked={consented} onChange={e => setConsented(e.target.checked)} style={{ marginTop: 3 }} />
        <span style={{ fontSize: 12, color: "#666", lineHeight: 1.5 }}>
          I agree to BrandHives' <a href="/terms" target="_blank" style={{ color: G }}>Terms of Service</a> and <a href="/privacy" target="_blank" style={{ color: G }}>Privacy Policy</a>, and consent to having my LinkedIn posts managed through this workspace.
        </span>
      </label>

      {alreadyInWorkspace && (
        <div style={{ padding: 16, backgroundColor: "#FEF3C7", border: "1px solid #F59E0B", borderRadius: 8, marginBottom: 16, color: "#92400E" }}>
          <strong>You're already in a workspace.</strong>
          <p style={{ marginTop: 8, marginBottom: 8, fontSize: 14 }}>
            You can only be in one workspace at a time. Leave your current workspace first.
          </p>
          <a href="#/settings" style={{ color: "#92400E", textDecoration: "underline", fontWeight: 600, fontSize: 14 }}>
            Go to settings
          </a>
        </div>
      )}

      <div style={{ display: "flex", gap: 12 }}>
        <button
          onClick={handleDecline}
          style={{ flex: "0 0 auto", background: "transparent", color: "#555", border: "1px solid #ddd", borderRadius: 8, padding: "14px 20px", fontSize: 15, fontWeight: 600, cursor: "pointer" }}
        >
          Decline
        </button>
        <button
          onClick={handleAccept}
          disabled={accepting || !consented || alreadyInWorkspace}
          style={{ flex: 1, background: G, color: "#fff", border: "none", borderRadius: 8, padding: "14px", fontSize: 15, fontWeight: 600, cursor: "pointer", opacity: (accepting || !consented || alreadyInWorkspace) ? 0.6 : 1 }}
        >
          {accepting ? "Joining..." : "Accept & Join Team"}
        </button>
      </div>
    </div>
  );
}
